import React from 'react';
import { cn } from '../utils';

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  checkboxSize?: 'xs' | 'sm' | 'md' | 'lg';
  color?: 'primary' | 'secondary' | 'accent' | 'success' | 'warning' | 'info' | 'error';
  indeterminate?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, checkboxSize = 'md', color, indeterminate = false, className, ...props }, ref) => {
    const checkboxRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => checkboxRef.current!);

    React.useEffect(() => {
      if (checkboxRef.current) {
        checkboxRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const checkboxClasses = cn(
      'checkbox',
      checkboxSize === 'xs' && 'checkbox-xs',
      checkboxSize === 'sm' && 'checkbox-sm',
      checkboxSize === 'lg' && 'checkbox-lg',
      color === 'primary' && 'checkbox-primary',
      color === 'secondary' && 'checkbox-secondary',
      color === 'accent' && 'checkbox-accent',
      color === 'success' && 'checkbox-success',
      color === 'warning' && 'checkbox-warning',
      color === 'info' && 'checkbox-info',
      color === 'error' && 'checkbox-error',
      className
    );

    if (label) {
      return (
        <label className="label cursor-pointer">
          <input ref={checkboxRef} type="checkbox" className={checkboxClasses} {...props} />
          <span className="label-text ml-2">{label}</span>
        </label>
      );
    }

    return <input ref={checkboxRef} type="checkbox" className={checkboxClasses} {...props} />;
  }
);

Checkbox.displayName = 'Checkbox';
